'use client';

import { __awaiter, __generator, __assign } from '../../_virtual/_tslib.js';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { EResumeType, ESearchable } from './types.js';
export { EResumeType, ESearchable } from './types.js';
import { ERROR_MESSAGE, SUCCESS_MESSAGE, SUCCESS_CODE } from './const.js';
import { ELanguage } from '../../Utils/constants.js';
import { getResumeSettingInfo, makeRefreshProfile, updateProfileCookie, makeSearchable } from '../../services/resumes/index.js';
import ModelError from '../../services/model/index.js';
import { ERefreshProfile, EOnOffStatus } from '../../services/resumes/types.js';

function useMakeSearchable(_a) {
    var _this = this;
    var data = _a.data, accessToken = _a.accessToken, _b = _a.langCode, langCode = _b === void 0 ? ELanguage.VI : _b, baseMsUrl = _a.baseMsUrl, _c = _a.isFetchData, isFetchData = _c === void 0 ? false : _c, _d = _a.isUpdateCookies, isUpdateCookies = _d === void 0 ? true : _d;
    var _e = useState(false), isSearchable = _e[0], setIsSearchable = _e[1];
    var _f = useState(), resumesInfo = _f[0], setResumeInfo = _f[1];
    // Avoid infinity loop when using useEffect with setState in Project career-center-container
    // To switch back and forth between dataProps and dataState of the resume
    var memoInitDataSearchable = useMemo(function () {
        var isHaveDataState = (resumesInfo === null || resumesInfo === void 0 ? void 0 : resumesInfo.resumes) && resumesInfo.resumes.length > 0;
        if (isHaveDataState) {
            return resumesInfo;
        }
        if ((data === null || data === void 0 ? void 0 : data.resumes) && data.resumes.length > 0 && !isHaveDataState) {
            return data;
        }
        return null;
    }, [resumesInfo, data]);
    // check have resumes or not
    var isEmptyResumes = !(memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.resumes) || (memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.resumes.length) < 1;
    // separate attachments & profile online in resumes
    var memoResumes = useMemo(function () {
        // profile online
        var profileOnline = memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.resumes.find(function (e) { return e.isAttach === EResumeType.ProfileOnline && (e === null || e === void 0 ? void 0 : e.allowSearchableDisplay) === ESearchable.On; });
        // attachments
        var attachments = memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.resumes.filter(function (e) { return e.isAttach === EResumeType.Attach; });
        return {
            profileOnline: profileOnline,
            attachments: attachments
        };
    }, [memoInitDataSearchable]);
    // check have attachment or not
    var isEmptyAttachment = !memoResumes.attachments || memoResumes.attachments.length < 1;
    // this value Check if the resume is  contactable or not
    var isUnContactable = useMemo(function () {
        return !!(memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.unContactable);
    }, [memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.unContactable]);
    // this value Check if the attach is eligible or not
    var isAttachAllowSearchable = useMemo(function () {
        var _a;
        return !!((_a = memoResumes.attachments) === null || _a === void 0 ? void 0 : _a.find(function (e) { return e.allowSearchableDisplay === ESearchable.On; }));
    }, [memoResumes.attachments]);
    // this value Check if the resume is eligible or not
    var isAllowSearchable = useMemo(function () {
        // check resumes file & searchable status
        if (isEmptyResumes) {
            return false;
        }
        if ((memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.searchableStatus) === ESearchable.Off) {
            return false;
        }
        // if user not have profile online | attachment. This resume is not allow searchable
        if (!memoResumes.profileOnline && isEmptyAttachment) {
            return false;
        }
        // if have attach cvs & attach cv not allow searchable
        if (!memoResumes.profileOnline && !isAttachAllowSearchable) {
            return false;
        }
        return true;
    }, [isEmptyResumes, isEmptyAttachment, memoInitDataSearchable, memoResumes, isAttachAllowSearchable]);
    // searchable priority attachment | online profile
    var priorityResume = useMemo(function () {
        var _a, _b;
        // if not allow searchable, can't not turn on
        if (isEmptyResumes) {
            return null;
        }
        var attachments = memoResumes.attachments, profileOnline = memoResumes.profileOnline;
        var isLastSearchableResumeId = (_b = (_a = memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.profileSetting) === null || _a === void 0 ? void 0 : _a.resumeId) !== null && _b !== void 0 ? _b : 0;
        var lastResumeMakeSearchable = memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.resumes.find(function (e) { return e.id === isLastSearchableResumeId; });
        // priority last resume make searchable
        if (lastResumeMakeSearchable) {
            return lastResumeMakeSearchable;
        }
        // priority attachment
        if (attachments && !isEmptyAttachment && isAttachAllowSearchable) {
            var lastUploadAttachment = attachments[0];
            return lastUploadAttachment;
        }
        // priority online profile
        if ((isEmptyAttachment || !isAttachAllowSearchable) && profileOnline) {
            return profileOnline;
        }
        return null;
    }, [isEmptyAttachment, isEmptyResumes, memoResumes, memoInitDataSearchable, isAttachAllowSearchable]);
    // this value Check if the profile is searchable or not
    var memoIsSearchable = useMemo(function () {
        // searchable status lets you know whether a record is searchable or not
        if (isEmptyResumes || (memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.searchableStatus) === ESearchable.Off)
            return false;
        // find resume is searchable
        var resumeSearchable = memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.resumes.find(function (e) { return e.isSearchable === ESearchable.On; });
        if (resumeSearchable) {
            return true;
        }
        return false;
    }, [memoInitDataSearchable, isEmptyResumes]);
    /**
     * @return latest resume is turn on searchable
     */
    var memoLatestSearchableResume = useMemo(function () {
        if (!(data === null || data === void 0 ? void 0 : data.resumes)) {
            return null;
        }
        var filterResumeIsSearchable = data === null || data === void 0 ? void 0 : data.resumes.filter(function (resume) { return resume.isSearchable === 1; });
        if (filterResumeIsSearchable.length === 0) {
            return null;
        }
        return filterResumeIsSearchable[0];
    }, [data === null || data === void 0 ? void 0 : data.resumes]);
    // when props resumes data is undefined
    // & has props isFetch === true
    var getResume = function () { return __awaiter(_this, void 0, void 0, function () {
        var resumeSettingInfo, data_1, resumeInfo, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, getResumeSettingInfo(accessToken, baseMsUrl !== null && baseMsUrl !== void 0 ? baseMsUrl : '', langCode)];
                case 1:
                    resumeSettingInfo = _a.sent();
                    if (resumeSettingInfo && resumeSettingInfo.data) {
                        data_1 = resumeSettingInfo.data;
                        resumeInfo = {
                            resumes: data_1 === null || data_1 === void 0 ? void 0 : data_1.attributes,
                            profileSetting: data_1 === null || data_1 === void 0 ? void 0 : data_1.profileSetting,
                            searchableStatus: data_1 === null || data_1 === void 0 ? void 0 : data_1.searchableStatus,
                            unContactable: data_1 === null || data_1 === void 0 ? void 0 : data_1.unContactable
                        };
                        setResumeInfo(resumeInfo);
                        return [2 /*return*/];
                    }
                    return [3 /*break*/, 3];
                case 2:
                    error_1 = _a.sent();
                    console.error(error_1);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    // query api make searchable
    var doSeachable = function (resumeId, searchAble, resumeType) { return __awaiter(_this, void 0, void 0, function () {
        var body, response, bodyProfileCookie, profileCookieRespone, type, bodyRefreshProfile, error_2;
        var _a, _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 7, , 8]);
                    setIsSearchable(!!searchAble);
                    body = {
                        resumeId: resumeId,
                        searchAble: searchAble
                    };
                    return [4 /*yield*/, makeSearchable(baseMsUrl, accessToken, body, langCode)];
                case 1:
                    response = _e.sent();
                    if (!((response === null || response === void 0 ? void 0 : response.code) === SUCCESS_CODE)) return [3 /*break*/, 6];
                    bodyProfileCookie = {
                        chooseCV: resumeType === EResumeType.Attach ? 'attach-cv' : 'profile',
                        resumeId: resumeId,
                        chooseRefreshType: (_b = (_a = memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.profileSetting) === null || _a === void 0 ? void 0 : _a.chooseRefreshType) !== null && _b !== void 0 ? _b : ERefreshProfile.Unset
                    };
                    profileCookieRespone = null;
                    if (!(searchAble === ESearchable.On && isUpdateCookies)) return [3 /*break*/, 3];
                    return [4 /*yield*/, updateProfileCookie(baseMsUrl, accessToken, bodyProfileCookie, langCode)];
                case 2:
                    profileCookieRespone = _e.sent();
                    _e.label = 3;
                case 3:
                    if (!(searchAble === ESearchable.Off)) return [3 /*break*/, 5];
                    type = (_d = (_c = memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.profileSetting) === null || _c === void 0 ? void 0 : _c.chooseRefreshType) !== null && _d !== void 0 ? _d : ERefreshProfile.Unset;
                    bodyRefreshProfile = { type: type, status: EOnOffStatus.Off };
                    return [4 /*yield*/, makeRefreshProfile(baseMsUrl, accessToken, bodyRefreshProfile, langCode)];
                case 4:
                    _e.sent();
                    _e.label = 5;
                case 5:
                    setIsSearchable(!!searchAble);
                    // renew resumes using isFetch props
                    if (isFetchData) {
                        if (!data || data.resumes.length < 1) {
                            getResume();
                        }
                    }
                    return [2 /*return*/, {
                            message: SUCCESS_MESSAGE,
                            lastMakeSearchableInfo: profileCookieRespone && (profileCookieRespone === null || profileCookieRespone === void 0 ? void 0 : profileCookieRespone.data)
                                ? __assign(__assign({}, memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.profileSetting), bodyProfileCookie) : null
                        }];
                case 6:
                    setIsSearchable(!isSearchable);
                    return [2 /*return*/, new ModelError({
                            data: response,
                            error: {
                                details: {
                                    ERROR_SEARCHABLE: 'ERROR_SEARCHABLE'
                                },
                                message: response === null || response === void 0 ? void 0 : response.message,
                                status: response === null || response === void 0 ? void 0 : response.code
                            }
                        })];
                case 7:
                    error_2 = _e.sent();
                    setIsSearchable(!isSearchable);
                    console.error(error_2);
                    return [2 /*return*/, new ModelError({
                            data: error_2,
                            error: {
                                details: {
                                    ERROR_SEARCHABLE: 'ERROR_SEARCHABLE'
                                },
                                message: ERROR_MESSAGE
                            }
                        })];
                case 8: return [2 /*return*/];
            }
        });
    }); };
    // get type of resume for update profile setting cookie when change resume searchable
    var getResumeType = function (resumeId) {
        var _a, _b;
        var resumeType = (_b = (_a = memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.resumes) === null || _a === void 0 ? void 0 : _a.find(function (e) { return e.id === resumeId; })) === null || _b === void 0 ? void 0 : _b.isAttach;
        return resumeType;
    };
    // handle turn on/off searchable
    // params value* is requied, using for on/off searchable
    // params resume is optional, using for case:
    //  + not using useMakeSearchable Hook in checkbox | switcher | func(value:boolean)=> any,
    //  + hard on/off | change resume searchable (attach | online) when clicked in each element
    var callbackMakeSearchable = useCallback(function (value, resumeId) { return __awaiter(_this, void 0, void 0, function () {
        var profileOnline, attachments, isLastSearchableResumeId, lastResumeMakeSearchable, lastAttachment, resumeSearchable, _a, respone, respone, respone, isNotAllowLastResumeMakeSearchable, respone_1, respone, response;
        var _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    profileOnline = memoResumes.profileOnline, attachments = memoResumes.attachments;
                    isLastSearchableResumeId = (_c = (_b = memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.profileSetting) === null || _b === void 0 ? void 0 : _b.resumeId) !== null && _c !== void 0 ? _c : 0;
                    lastResumeMakeSearchable = memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.resumes.find(function (e) { return e.id === isLastSearchableResumeId; });
                    lastAttachment = attachments && (attachments === null || attachments === void 0 ? void 0 : attachments.length) > 0 ? attachments[0] : null;
                    resumeSearchable = memoInitDataSearchable === null || memoInitDataSearchable === void 0 ? void 0 : memoInitDataSearchable.resumes.find(function (e) { return e.isSearchable === ESearchable.On; });
                    _a = value;
                    switch (_a) {
                        case true: return [3 /*break*/, 1];
                        case false: return [3 /*break*/, 12];
                    }
                    return [3 /*break*/, 15];
                case 1:
                    // if not allow searchable, can't not turn on
                    if (!isAllowSearchable) {
                        return [2 /*return*/, new ModelError({
                                data: null,
                                error: {
                                    details: {
                                        ALLOW_SEARCHABLE: isAllowSearchable
                                    },
                                    message: ERROR_MESSAGE
                                }
                            })];
                    }
                    if (!resumeId) return [3 /*break*/, 3];
                    return [4 /*yield*/, doSeachable(resumeId, ESearchable.On, getResumeType(resumeId))];
                case 2:
                    respone = _e.sent();
                    return [2 /*return*/, respone];
                case 3:
                    if (!!lastResumeMakeSearchable) return [3 /*break*/, 7];
                    if (!((lastAttachment === null || lastAttachment === void 0 ? void 0 : lastAttachment.id) && lastAttachment.allowSearchableDisplay === ESearchable.On)) return [3 /*break*/, 5];
                    return [4 /*yield*/, doSeachable(lastAttachment.id, ESearchable.On, EResumeType.Attach)];
                case 4:
                    respone = _e.sent();
                    return [2 /*return*/, respone];
                case 5:
                    if (!((profileOnline === null || profileOnline === void 0 ? void 0 : profileOnline.id) && (isEmptyAttachment || !isAttachAllowSearchable))) return [3 /*break*/, 7];
                    return [4 /*yield*/, doSeachable(profileOnline.id, ESearchable.On, EResumeType.ProfileOnline)];
                case 6:
                    respone = _e.sent();
                    return [2 /*return*/, respone];
                case 7:
                    if (!(lastResumeMakeSearchable === null || lastResumeMakeSearchable === void 0 ? void 0 : lastResumeMakeSearchable.id)) return [3 /*break*/, 11];
                    isNotAllowLastResumeMakeSearchable = lastResumeMakeSearchable.allowSearchableDisplay === ESearchable.Off;
                    if (!(lastResumeMakeSearchable.isAttach === EResumeType.ProfileOnline &&
                        isNotAllowLastResumeMakeSearchable)) return [3 /*break*/, 9];
                    return [4 /*yield*/, doSeachable((_d = lastAttachment === null || lastAttachment === void 0 ? void 0 : lastAttachment.id) !== null && _d !== void 0 ? _d : 0, ESearchable.On, EResumeType.ProfileOnline)];
                case 8:
                    respone_1 = _e.sent();
                    return [2 /*return*/, respone_1];
                case 9: return [4 /*yield*/, doSeachable(lastResumeMakeSearchable.id, ESearchable.On, getResumeType(lastResumeMakeSearchable === null || lastResumeMakeSearchable === void 0 ? void 0 : lastResumeMakeSearchable.id))];
                case 10:
                    respone = _e.sent();
                    return [2 /*return*/, respone];
                case 11: return [2 /*return*/, null];
                case 12:
                    if (!(resumeSearchable === null || resumeSearchable === void 0 ? void 0 : resumeSearchable.id)) return [3 /*break*/, 14];
                    return [4 /*yield*/, doSeachable(resumeSearchable.id, ESearchable.Off, getResumeType(resumeSearchable === null || resumeSearchable === void 0 ? void 0 : resumeSearchable.id))];
                case 13:
                    response = _e.sent();
                    return [2 /*return*/, response];
                case 14: return [2 /*return*/, null];
                case 15: return [2 /*return*/, null];
            }
        });
    }); }, [memoInitDataSearchable, isEmptyAttachment, memoResumes, isAllowSearchable, getResumeType]);
    useEffect(function () {
        if (typeof memoIsSearchable === 'boolean') {
            setIsSearchable(memoIsSearchable);
        }
    }, [memoIsSearchable]);
    useEffect(function () {
        if (isFetchData) {
            if (!data || data.resumes.length < 1) {
                getResume();
            }
        }
    }, [isFetchData, accessToken]);
    return [
        {
            isUnContactable: isUnContactable,
            isSearchable: isSearchable,
            isAllowSearchable: isAllowSearchable,
            isAttachAllowSearchable: isAttachAllowSearchable
        },
        callbackMakeSearchable,
        priorityResume,
        memoLatestSearchableResume
    ];
}

export { useMakeSearchable as default };
//# sourceMappingURL=index.js.map
